/*
 * ImportModal
 *
 * Modal component for importing data from CSV files with preview functionality
 */

import React, { useState, useMemo } from 'react';
import { Alert, Container } from 'reactstrap';
import { AgGridReact } from 'ag-grid-react';
import { AllCommunityModule } from 'ag-grid-community';
import styled from 'styled-components';
import DropArea from '../../../common/DropArea';
import ConfirmationModal from '../../../common/ConfirmationModal';
import { ImportModalProps } from '../interfaces';
import { useSelector } from 'react-redux';
import { MappingAdminState } from '../../../common/domain/mappingAdmin';
import {
  COLUMN_IDS_FOR_FILE_UPLOAD,
  TABS,
  NON_EMPTY_VALIDATION_COLUMN_IDS,
  COLUMN_ID,
  DUPLICATE_VALIDATION_COLUMN_IDS,
  FIXING_PROXY_FIELD_NAME_TO_COLUMN_ID,
} from '../constants';
import { getTabColumns } from '../gridColumns';
import {
  findDuplicateId,
  isISOFormattedTimestamp,
  isValidTimestamp,
} from '../utils';

const GridContainer = styled.div`
  height: 50vh;
  width: 100%;
  margin-top: 1rem;
`;

const ImportModal = ({ isOpen, onClose, onImport }: ImportModalProps) => {
  const {
    activeTab,
    dataGroups,
    ratesData,
    saving,
    mappingDataValues,
  } = useSelector((state: any) => state.mappingAdmin as MappingAdminState);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [parsedData, setParsedData] = useState<any[] | null>(null);
  const [error, setError] = useState<string>('');

  const columnDefinitions = useMemo(() => {
    const allColumns: Array<{
      field: COLUMN_ID;
      [key: string]: any;
    }> = getTabColumns(mappingDataValues, dataGroups)[activeTab]();

    return allColumns.filter((column) =>
      COLUMN_IDS_FOR_FILE_UPLOAD[activeTab].includes(column.field),
    );
  }, [activeTab, mappingDataValues, dataGroups]);

  const handleClose = () => {
    setSelectedFile(null);
    setParsedData(null);
    setError('');
    onClose();
  };

  /**
   * Parse a single CSV line respecting quoted fields (RFC 4180).
   * Commas inside double-quoted fields are not treated as delimiters,
   * so e.g. "2126-01-25T21:37:32,000Z" stays one value (Excel/locale comma).
   */
  const parseRowData = (line: string): string[] => {
    const result: string[] = [];
    let current = '';
    let inQuotes = false;
    const trimmedLine = line.trimEnd().replace(/\r$/, '');
    for (let i = 0; i < trimmedLine.length; i += 1) {
      const char = trimmedLine[i];
      if (char === '"') {
        if (inQuotes && trimmedLine[i + 1] === '"') {
          current += '"';
          i += 1;
        } else {
          inQuotes = !inQuotes;
        }
      } else if (char === ',' && !inQuotes) {
        result.push(current.trim());
        current = '';
      } else {
        current += char;
      }
    }
    result.push(current.trim());
    return result;
  };

  // ToDo: Break large chunk of validation logic into smaller components
  const validateRowData = (
    rowData: any[],
    tab: string,
    columnNameMapping: Record<COLUMN_ID, string>,
  ) => {
    const errors: string[] = [];
    const nonEmptyColumns = NON_EMPTY_VALIDATION_COLUMN_IDS[tab] || [];
    const allowedGroups = Array.isArray(dataGroups) ? dataGroups : [];

    const addValueError = (
      rowNumber: number,
      columnId: string,
      value: string,
    ) => {
      errors.push(
        `Row ${rowNumber}: value ${value} for column ${columnNameMapping[columnId]} is not valid.`,
      );
    };

    const uniqueColumnNames = DUPLICATE_VALIDATION_COLUMN_IDS[activeTab]
      .map((columnId) => columnNameMapping[columnId])
      .join(', ');

    rowData.forEach((row, index) => {
      const otherRows = rowData.slice(index + 1);
      const duplicatedNodeIdInCsv = findDuplicateId(row, otherRows, activeTab);
      if (duplicatedNodeIdInCsv) {
        errors.push(
          `The CSV file contains duplicate rows with the same values for ${uniqueColumnNames}.`,
        );
        return;
      }
    });

    rowData.forEach((row, index) => {
      const rowNumber = index + 2;
      const groupIdColumn =
        tab === TABS.LEGAL_ENTITY
          ? COLUMN_ID.MAPPED_GROUP_ID
          : COLUMN_ID.GROUP_ID;
      if (row[groupIdColumn] && !allowedGroups.includes(row[groupIdColumn])) {
        addValueError(rowNumber, groupIdColumn, row[groupIdColumn]);
      }

      if (tab === TABS.RATES_FIXING) {
        for (const [fieldName, columnId] of Object.entries(
          FIXING_PROXY_FIELD_NAME_TO_COLUMN_ID,
        )) {
          const value = row[columnId];
          if (value && !mappingDataValues[fieldName].includes(value)) {
            addValueError(rowNumber, columnId, value);
          }
        }
      } else if (tab === TABS.RATES_FIELD) {
        if (!mappingDataValues[row[COLUMN_ID.FIELD_NAME]]) {
          addValueError(
            rowNumber,
            COLUMN_ID.FIELD_NAME,
            row[COLUMN_ID.FIELD_NAME],
          );
        } else {
          if (
            !mappingDataValues[row[COLUMN_ID.FIELD_NAME]].includes(
              row[COLUMN_ID.MAPPED_VALUE],
            )
          ) {
            addValueError(
              rowNumber,
              COLUMN_ID.MAPPED_VALUE,
              row[COLUMN_ID.MAPPED_VALUE],
            );
          }
        }
      }

      nonEmptyColumns.forEach((columnId: string) => {
        const value = row[columnId];
        if (!value || value.toString().trim() === '') {
          errors.push(
            `Row ${rowNumber}: ${columnNameMapping[columnId]} cannot be empty.`,
          );
        }
      });

      const timestampColumns = [
        COLUMN_ID.VALID_FROM,
        COLUMN_ID.VALID_TO,
      ] as const;
      for (const columnId of timestampColumns) {
        const value = row[columnId];
        if (value != null && value.toString().trim() !== '') {
          if (!isISOFormattedTimestamp(value)) {
            errors.push(
              `Row ${rowNumber}: ${columnNameMapping[columnId]} is not a valid timestamp format. Please use a valid date format (e.g. ISO 8601: YYYY-MM-DDTHH:mm:ss).`,
            );
          } else if (!isValidTimestamp(value)) {
            errors.push(
              `Row ${rowNumber}: ${columnNameMapping[columnId]} is not a valid date.`,
            );
          }
        }
      }

      if (
        new Date(row[COLUMN_ID.VALID_TO]) <= new Date(row[COLUMN_ID.VALID_FROM])
      ) {
        errors.push(
          `Row ${rowNumber}: ${
            columnNameMapping[COLUMN_ID.VALID_FROM]
          } must be before ${columnNameMapping[COLUMN_ID.VALID_TO]}`,
        );
      }

      const duplicatedNodeId = findDuplicateId(
        row,
        Object.values(ratesData[activeTab]),
        activeTab,
      );
      if (duplicatedNodeId) {
        errors.push(
          `Row ${rowNumber}: mapping data with the same values for ${uniqueColumnNames} already exists.`,
        );
      }
    });

    if (errors.length > 0) {
      throw new Error(`Validation failed:\n${errors.join('\n')}`);
    }
  };

  const parseCSV = (columnDefinitions: any[], csvText: string) => {
    const lines = csvText.trim().split(/\r?\n/);
    if (lines.length < 2) {
      throw new Error(
        'The CSV file must contain a header row and at least one data row.',
      );
    }
    const csvHeaders = parseRowData(lines[0]);
    const requiredHeaders = columnDefinitions.map(
      (column) => column.headerName,
    );
    const missingHeaders = requiredHeaders.filter(
      (field) => !csvHeaders.includes(field),
    );
    if (missingHeaders.length > 0) {
      throw new Error(
        `Missing required columns: ${missingHeaders.join(', ')}.`,
      );
    }
    const rowData = lines
      .slice(1)
      .map((line, index) => {
        const values = parseRowData(line);
        const row: any = { id: index };
        columnDefinitions.forEach((column) => {
          const columnIndex = csvHeaders.indexOf(column.headerName);
          row[column.field] = values[columnIndex] || '';
        });
        return row;
      })
      .filter((row) => {
        const nonEmptyValues = Object.values(row).filter(
          (val) => val !== '' && val != null,
        );
        return nonEmptyValues.length > 1;
      });
    if (rowData.length === 0) {
      throw new Error('CSV file contains no valid data rows.');
    }
    return rowData;
  };

  const handleFileSelected = async (file: File) => {
    setSelectedFile(file);
    setError('');

    const columnNameMapping = columnDefinitions.reduce((result, column) => {
      result[column.field] = column.headerName;
      return result;
    }, {} as Record<COLUMN_ID, string>);

    try {
      const text = await file.text();
      const parsed = parseCSV(columnDefinitions, text);
      validateRowData(parsed, activeTab, columnNameMapping);
      setParsedData(parsed);
    } catch (error: any) {
      setError(
        error.message ||
          'Error parsing CSV file, please check the file and try again.',
      );
    }
  };

  const handleImport = async () => {
    if (parsedData) {
      await onImport(parsedData);
      handleClose();
    }
  };

  const renderContent = () => (
    <Container>
      {error && (
        <Alert
          color="danger"
          className="mb-3"
          style={{ whiteSpace: 'pre-line' }}
        >
          {error}
        </Alert>
      )}
      {parsedData && selectedFile ? (
        <>
          <div className="mb-3">
            <strong>File:</strong> {selectedFile.name} ({parsedData.length} row
            {parsedData.length > 1 ? 's' : ''})
          </div>
          <GridContainer className="ag-theme-alpine">
            <AgGridReact
              rowData={parsedData}
              columnDefs={columnDefinitions}
              defaultColDef={{ resizable: true, sortable: true }}
              modules={[AllCommunityModule]}
            />
          </GridContainer>
        </>
      ) : (
        <DropArea
          onFileSelected={handleFileSelected}
          file={selectedFile || undefined}
          withButton={true}
        />
      )}
    </Container>
  );

  return (
    <ConfirmationModal
      isOpen={isOpen}
      title="Import from File"
      message={renderContent()}
      confirmText={
        parsedData && selectedFile
          ? `Import Data (${parsedData.length} row${
              parsedData.length > 1 ? 's' : ''
            })`
          : ''
      }
      onConfirm={handleImport}
      onCancel={handleClose}
      confirmColor="primary"
      cancelColor="secondary"
      size="xl"
      loading={saving}
    />
  );
};

export default ImportModal;
